import { observable } from 'mobx';
import { useEffect, useState } from 'react';

import noticeError from '../functions/noticeError';
import useCalculator from './useCalculator';
import useFieldset from './useFieldset';


function useCalculatorStepState({
    number,
    hasCompleteButton,
    onCheckComplete,
    onPreviousStepConfirmComplete,
}) {
    const calculator = useCalculator();
    const fieldset = useFieldset();

    if (calculator == null) {
        const error = new Error('[useCalculatorStepState] Cannot create Calculator Step without Calculator context');

        noticeError(error);
        throw error;
    }

    const [ state ] = useState(() => observable({
        calculator,
        fieldset,

        number,

        // If we have no complete button, we can assume we're confirmed complete since
        //  we require no user interaction to complete us
        hasConfirmedComplete: hasCompleteButton ? false : true,
        onCheckComplete,
        // This is used for steps that require action once a previous
        //  step has confirmed their completion (via complete button)
        onPreviousStepConfirmComplete,

        get canConfirmComplete() {
            // REQUIREMENT: if any of our fields are invalid, we're not complete.
            if (state.fieldset && state.fieldset.isValid === false) {
                return false;
            }

            // REQUIREMENT: if we've been given an override, use it.
            return typeof state.onCheckComplete === 'function'
                ? state.onCheckComplete(state.fieldset)
                : true;
        },
        get isComplete() {
            return state.canConfirmComplete
                && state.hasConfirmedComplete
                // Due to the possibility of React rendering steps out of order,
                // We can't safely throw if the previous step isn't found
                && state.hasCompletedPreviousStep;
        },
        get hasCompletedPreviousStep() {
            return state.calculator.hasCompletedPreviousStep(state.number, false);
        },

        confirmComplete() {
            // Toggle complete property when the CTA is clicked
            state.hasConfirmedComplete = true;

            // Activate next step if applicable
            const nextStep = calculator.getNextStep(state.number);
            nextStep?.onPreviousStepConfirmComplete?.();
        },

        setHasConfirmedComplete(hasConfirmedComplete) {
            state.hasConfirmedComplete = hasConfirmedComplete;
        },
        setOnCheckComplete(onCheckComplete) {
            state.onCheckComplete = onCheckComplete;
        },
        setOnPreviousStepConfirmComplete(onPreviousStepConfirmComplete) {
            state.onPreviousStepConfirmComplete = onPreviousStepConfirmComplete;
        },
    }));

    // Add on mount, remove on unmount.
    useEffect(() => {
        calculator.addStep(state);

        return () => calculator.removeStep(state.number);
    }, [ calculator, state ]);

    // When we do have a complete button, we want hasConfirmedComplete to be evaluated
    //  when the user clicks the button. Otherwise with no button we can assume complete is confirmed
    useEffect(() => {
        state?.setHasConfirmedComplete(!hasCompleteButton);
    }, [ state, hasCompleteButton ]);

    // When onCheckComplete changes we want to reflect the change in its calculator step
    useEffect(() => {
        state?.setOnCheckComplete(onCheckComplete);
    }, [ state, onCheckComplete ]);

    // When onPreviousStepConfirmComplete changes we want to reflect the change in its calculator step
    useEffect(() => {
        state?.setOnPreviousStepConfirmComplete(onPreviousStepConfirmComplete);
    }, [ state, onPreviousStepConfirmComplete ]);

    return state;
}

export default useCalculatorStepState;